/*
 * Лента истории человека: касания по времени и полоса готовности.
 *
 * 🔴 Прочерк и ноль — разные вещи. `readiness.measured === false` означает,
 * что сверка не запускалась ни разу; ноль означал бы «запускалась и дала
 * ноль». Полоса рисует прочерк, а не пустую шкалу: адвокату важно отличать
 * «не смотрели» от «смотрели и пусто».
 *
 * 🔴 Пустая лента говорит словами, а не выглядит пустотой. И `note` из ответа
 * выводится всегда: сейчас он сообщает, что документов в ленте нет не потому,
 * что их нет у клиента, а потому что сканы пока не сохраняются.
 */
import React from 'react';
import './HistoryTimeline.css';
// 🔴 ВТОРОГО ДЕРЖАТЕЛЯ МЕТОК БОЛЬШЕ НЕТ. Локальный словарь жил здесь и разошёлся
//    бы с новым экраном молча — на первой же правке одной из копий. Метки
//    перенесены в config/historyEvents.js БУКВА В БУКВУ, включая мёртвую
//    `checklist_verified` (писателей 0): отрисовка этой страницы у fitenko и у
//    MLG обязана не измениться ни на символ, и это доказывают ворота слов
//    (slovar-ist.py, С3 — посимвольное равенство на объединении ключей).
import { metkaTipa } from '../../config/historyEvents';
import { useUiLanguage } from '../../context/UiLanguageContext';
import { formatEventTitle } from '../../utils/historyFormatters';

const formatWhen = (iso, lang) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const locale = lang === 'ru' ? 'ru-RU' : 'en-US';
    return d.toLocaleString(locale, {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const ReadinessBar = ({ readiness }) => {
    const { t } = useUiLanguage();
    if (!readiness || readiness.measured !== true) {
        return (
            <div className="history-readiness history-readiness--unmeasured">
                <div className="history-readiness__row">
                    <span className="history-readiness__label">{t('practice.history.readiness_label', 'Filing Readiness')}</span>
                    <span className="history-readiness__value">—</span>
                </div>
                <p className="history-readiness__hint">
                    {t('practice.history.readiness_unmeasured_hint', 'Reconciliation has not been run. A dash is not zero percent.')}
                </p>
            </div>
        );
    }
    const pct = typeof readiness.completion === 'number' ? readiness.completion : null;
    return (
        <div className="history-readiness">
            <div className="history-readiness__row">
                <span className="history-readiness__label">{t('practice.history.readiness_label', 'Filing Readiness')}</span>
                <span className="history-readiness__value">{pct === null ? '—' : `${pct}%`}</span>
            </div>
            <div className="history-readiness__track">
                <div className="history-readiness__fill" style={{ width: `${pct || 0}%` }} />
            </div>
            <div className="history-readiness__meta">
                <span>{t('practice.history.attached', 'Attached: {pct}', { pct: readiness.attached === null || readiness.attached === undefined ? '—' : `${readiness.attached}%` })}</span>
                <span>{t('practice.history.risk', 'Risk: {risk}', { risk: readiness.risk_level || '—' })}</span>
                <span>{t('practice.history.ready_for_filing', 'Ready for filing: {ready}', {
                    ready: readiness.ready_for_filing === null || readiness.ready_for_filing === undefined
                        ? '—'
                        : (readiness.ready_for_filing ? t('practice.history.yes', 'yes') : t('practice.history.no', 'no'))
                })}</span>
            </div>
            {readiness.last_action && (
                <p className="history-readiness__hint">
                    {t('practice.history.last_action_label', 'Last action:')}{' '}
                    <span data-user-content="true">{readiness.last_action}</span>
                </p>
            )}
        </div>
    );
};

const HistoryTimeline = ({ history, loading, error }) => {
    const { t, lang } = useUiLanguage();
    if (loading) return <div className="history-timeline__state">{t('practice.history.loading', 'Loading history...')}</div>;
    if (error) {
        return (
            <div className="history-timeline__state history-timeline__state--error">
                {t('practice.history.error', 'Failed to load history: {error}', { error })}
            </div>
        );
    }
    if (!history) return null;

    const events = history.events || [];

    const formatEventType = (tip) => {
        if (lang === 'ru') return metkaTipa(tip);
        const key = `practice.history_event.${tip}`;
        const val = t(key);
        return val !== key ? val : metkaTipa(tip);
    };

    return (
        <section className="history-timeline">
            <div className="history-timeline__header">
                <h2>{t('practice.history.title', 'History')}</h2>
                <span className="history-timeline__count">
                    {events.length === 0
                        ? t('practice.history.no_events', 'no touchpoints recorded yet')
                        : t('practice.history.events_count', '{count} entries', { count: events.length })}
                </span>
            </div>

            <ReadinessBar readiness={history.readiness} />

            {events.length === 0 ? (
                <p className="history-timeline__empty">
                    {t('practice.history.empty_timeline', 'No touchpoints in the feed. This means there are no records in the database, not that there was no communication.')}
                </p>
            ) : (
                <ol className="history-timeline__list">
                    {events.map((e) => (
                        <li key={e.id} className={`history-event history-event--${e.event_type}`}>
                            <div className="history-event__when">{formatWhen(e.created_at, lang)}</div>
                            <div className="history-event__body">
                                <span className="history-event__type">
                                    {formatEventType(e.event_type)}
                                </span>
                                <span className="history-event__title" data-user-content="true">
                                    {formatEventTitle(e.title, e.event_type, t, lang)}
                                </span>
                                {e.description && (
                                    <p className="history-event__desc" data-user-content="true">{e.description}</p>
                                )}
                            </div>
                        </li>
                    ))}
                </ol>
            )}

            {history.note && <p className="history-timeline__note" data-user-content="true">{history.note}</p>}
        </section>
    );
};

export default HistoryTimeline;
